import React from "react";

/**
 * lazyWithRetry - React.lazy() wrapper that recovers from ChunkLoadError.
 *
 * Dynamic imports fail with "ChunkLoadError: Loading chunk N failed" when the
 * chunk hash referenced by the currently-loaded app no longer exists on the
 * server (e.g. a new deployment happened while the tab was open) or when a
 * transient network blip interrupts the fetch. Left unhandled, this crashes
 * to the top-level error boundary the moment a user navigates to a
 * lazy-loaded route (e.g. clicking "Already have an account? Sign in").
 *
 * This wrapper retries the import a couple of times first (covers transient
 * network issues), and if it is still failing because the chunk is genuinely
 * stale, forces a single full page reload so the browser fetches the latest
 * build — which resolves the vast majority of real-world ChunkLoadErrors.
 */
const RELOAD_FLAG_KEY = "__vw_chunk_reload_attempted";

function isChunkLoadError(error) {
  const message = String(error?.message || error || "");
  return (
    error?.name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
}

export function lazyWithRetry(importFn, { retries = 2, retryDelayMs = 500 } = {}) {
  const load = async () => {
    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const mod = await importFn();
        // A successful load means the chunk is fine now — clear any reload flag.
        try { sessionStorage.removeItem(RELOAD_FLAG_KEY); } catch (e) { /* ignore */ }
        return mod;
      } catch (error) {
        lastError = error;
        if (!isChunkLoadError(error) || attempt === retries) break;
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs * (attempt + 1)));
      }
    }

    if (isChunkLoadError(lastError)) {
      // The chunk is genuinely stale (new deployment). Reload once to fetch
      // the latest build instead of surfacing a hard crash to the user.
      let alreadyReloaded = false;
      try { alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG_KEY) === "1"; } catch (e) { /* ignore */ }
      if (!alreadyReloaded) {
        try { sessionStorage.setItem(RELOAD_FLAG_KEY, "1"); } catch (e) { /* ignore */ }
        window.location.reload();
        // Return a never-resolving promise so React.lazy doesn't render an
        // error UI during the brief moment before the reload takes effect.
        return new Promise(() => {});
      }
    }

    throw lastError;
  };

  return React.lazy(load);
}

export default lazyWithRetry;
